/**
 * DeliveryChannels.jsx — Admin Delivery Channel Master.
 *
 *   Admin → Settings → Sales Configuration → Delivery Channels
 *
 * Groups centers by country. Each center shows its resolved active
 * channels; admins can toggle from the country default list, add
 * custom platforms (USA/Europe/UAE), and persist.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Loader2, Plus, Save, Globe2, Bike, X, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";

const flag = (country) => ({
  India: "🇮🇳",
  Australia: "🇦🇺",
  USA: "🇺🇸",
  UK: "🇬🇧",
  UAE: "🇦🇪",
  Japan: "🇯🇵",
  Singapore: "🇸🇬",
  Belgium: "🇧🇪",
  Europe: "🇪🇺",
})[country] || "🌐";

export default function DeliveryChannels() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // by center code
  const [byCountry, setByCountry] = useState({});
  const [countryDefaults, setCountryDefaults] = useState({});
  // Per-center working state: { [code]: { active_keys, custom_channels } }
  const [drafts, setDrafts] = useState({});

  const isAdmin = session?.is_super_admin || session?.is_admin;

  useEffect(() => {
    if (!session?.token || !isAdmin) return;
    (async () => {
      try {
        const [allRes, cRes] = await Promise.all([
          api.post("/delivery-channels/all", { token: session.token }),
          api.get("/delivery-channels/countries"),
        ]);
        setByCountry(allRes.data.by_country || {});
        const map = {};
        (cRes.data.countries || []).forEach((c) => { map[c.country] = c.channels; });
        setCountryDefaults(map);

        // Initialize drafts from resolved active channels
        const d = {};
        Object.values(allRes.data.by_country || {}).flat().forEach((c) => {
          d[c.code] = {
            active_keys: c.active_channels
              .filter((ch) => !ch.key.startsWith("__custom_"))
              .map((ch) => ch.key),
            custom_channels: c.active_channels
              .filter((ch) => (ch.field || "").startsWith("delivery_extra.") &&
                              !(map[c.country] || []).some((d2) => d2.key === ch.key))
              .map((ch) => ({ key: ch.key, label: ch.label })),
          };
        });
        setDrafts(d);
      } catch (e) {
        toast.error(`Failed to load: ${e.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [session?.token, isAdmin]);

  const toggleChannel = (code, channelKey) => {
    setDrafts((prev) => {
      const cur = prev[code] || { active_keys: [], custom_channels: [] };
      const set = new Set(cur.active_keys);
      set.has(channelKey) ? set.delete(channelKey) : set.add(channelKey);
      return { ...prev, [code]: { ...cur, active_keys: [...set] } };
    });
  };

  const addCustom = (code, country) => {
    const label = window.prompt(`Add custom delivery channel for ${code} (label):`);
    if (!label) return;
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (!key) return;
    setDrafts((prev) => {
      const cur = prev[code] || { active_keys: [], custom_channels: [] };
      if (cur.custom_channels.some((c) => c.key === key)) return prev;
      return { ...prev, [code]: { ...cur, custom_channels: [...cur.custom_channels, { key, label }] } };
    });
  };

  const removeCustom = (code, key) => {
    setDrafts((prev) => {
      const cur = prev[code] || { active_keys: [], custom_channels: [] };
      return { ...prev, [code]: { ...cur, custom_channels: cur.custom_channels.filter((c) => c.key !== key) } };
    });
  };

  const saveCenter = async (code) => {
    const draft = drafts[code];
    if (!draft) return;
    setSaving((s) => ({ ...s, [code]: true }));
    try {
      const res = await api.put(`/delivery-channels/center/${code}`, {
        token: session.token,
        active_keys: draft.active_keys,
        custom_channels: draft.custom_channels,
      });
      toast.success(`Saved ${code}`);
      // Update the byCountry so the row re-renders with resolved channels
      setByCountry((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((country) => {
          next[country] = next[country].map((c) =>
            c.code === code ? { ...c, active_channels: res.data.channels, has_override: true } : c
          );
        });
        return next;
      });
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving((s) => ({ ...s, [code]: false }));
    }
  };

  const countries = Object.keys(byCountry).sort();

  if (!isAdmin) {
    return <div className="p-6 text-center text-slate-500">Admin access required.</div>;
  }
  if (loading) return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="delivery-channels-admin">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-rose-100 text-rose-700"><Bike className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-bold">Delivery Channel Master</h1>
          <p className="text-sm text-slate-500">
            Configure which delivery platforms are active for each center. Used by Sales Dashboard,
            Reports, P&amp;L, GST, Commissions, and Net P/L calculations.
          </p>
        </div>
      </div>

      {countries.map((country) => {
        const defaults = countryDefaults[country] || [];
        return (
          <Card key={country} data-testid={`country-block-${country}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="text-xl leading-none">{flag(country)}</span>
                {country}
                <Badge variant="secondary" className="ml-2 text-xs">
                  {byCountry[country].length} center{byCountry[country].length === 1 ? "" : "s"}
                </Badge>
                <span className="text-xs text-slate-500 ml-2">
                  Defaults: {defaults.map((d) => d.label).join(", ") || "—"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {byCountry[country].map((c) => {
                const draft = drafts[c.code] || { active_keys: [], custom_channels: [] };
                const allChannels = [
                  ...defaults,
                  ...draft.custom_channels.map((ch) => ({ ...ch, __custom: true })),
                ];
                return (
                  <div key={c.code} className="border rounded-lg p-4 bg-slate-50/50" data-testid={`center-row-${c.code}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold">{c.code} · {c.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <Globe2 className="w-3 h-3" />
                          {c.has_override ? "Custom configuration" : "Inheriting country defaults"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => addCustom(c.code, country)} data-testid={`add-custom-${c.code}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Custom
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveCenter(c.code)}
                          disabled={saving[c.code]}
                          data-testid={`save-${c.code}`}
                        >
                          {saving[c.code] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1"/>}
                          Save
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {allChannels.map((ch) => {
                        const checked = draft.active_keys.includes(ch.key) || ch.__custom;
                        return (
                          <label
                            key={ch.key}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-sm ${
                              checked
                                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                : "bg-white border-slate-200 text-slate-600"
                            }`}
                            data-testid={`ch-${c.code}-${ch.key}`}
                          >
                            {!ch.__custom && (
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleChannel(c.code, ch.key)}
                                className="w-3.5 h-3.5"
                              />
                            )}
                            {ch.__custom && <Check className="w-3.5 h-3.5 text-emerald-600"/>}
                            <span>{ch.label}</span>
                            {ch.__custom && (
                              <button
                                type="button"
                                onClick={() => removeCustom(c.code, ch.key)}
                                className="ml-1 text-slate-400 hover:text-red-500"
                                title="Remove custom channel"
                                data-testid={`rm-${c.code}-${ch.key}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
